import React, { useState, useEffect, useCallback } from "react";
import { getComments } from "../services/comments";
import { RESTComment } from "../services/types";
import Comment from "../components/Comment";
import Button from "../components/Button";
import PostSkeleton from "../components/PostSkeleton";
import "./ModerationCommentsPage.css";

const ModerationCommentsPage: React.FC = () => {
    const [comments, setComments] = useState<RESTComment[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadComments = useCallback(async (page: number, append: boolean = false) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getComments(page);
            setComments(prevComments => append ? [...prevComments, ...response.comments] : response.comments);
            setTotalPages(response.totalPages);
        } catch (err: any) {
            setError(err.message || "Failed to load comments.");
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCommentDeleted = useCallback(() => {
        // Reload the current page to refresh the list
        loadComments(currentPage);
    }, [currentPage, loadComments]);

    useEffect(() => {
        loadComments(1);
    }, [loadComments]);

    const handleLoadMore = () => {
        if (currentPage < totalPages && !loading) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            loadComments(nextPage, true);
        }
    };

    return (
        <div className="ModerationCommentsPage">
            {error && <p className="error-message">{error}</p>}
            
            {comments.length > 0 && (
                <div className="comments-container">
                    <ul className="comments-list">
                        {comments.map(comment => (
                            <Comment 
                                key={comment.id} 
                                comment={comment}
                                onDeleted={handleCommentDeleted}
                                showPostLink={true}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {!loading && comments.length === 0 && (
                <p className="empty-message">Aucun commentaire trouvé.</p>
            )}

            {loading && (
                <div style={{ padding: '1rem' }}>
                    <PostSkeleton />
                    <PostSkeleton />
                    <PostSkeleton />
                </div>
            )}

            {currentPage < totalPages && !loading && (
                <div className="load-more-container">
                    <Button onClick={handleLoadMore} variant="secondary" size="medium">
                        Charger plus de commentaires
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ModerationCommentsPage;
